// src/finance/components/WhoPaysWhat.tsx
// Vue « Qui paie quoi » (spec 2026-08-20) : projection par payeur des prélèvements
// de la simulation courante — société (impôt société + patronales) vs perso France
// (salariales + IR + PFU). Présentation pure : aucun calcul fiscal ici, tout vient
// du breakdown du moteur ; l'agrégation est exportée pour être testée.
import React from 'react'
import type { CorporateRegime, FinanceDirection, PayerMode, RegimeResult } from '../engine/types'
import { round2 } from '../engine/tax'
import { aggregatePayers } from '../payers'
import { donutData } from '../donut'
import { RevenueDonut } from './RevenueDonut'
import { fmtEur } from '../format'
import { InfoTip } from './InfoTip'
import { TAX_INFO } from './taxInfo'

/** Alias historique : le type vit dans engine/types.ts, que FinanceSettings référence. */
export type ModeKey = PayerMode

const MODES: { key: ModeKey; label: string }[] = [
  { key: 'fullDividends', label: '100 % dividendes' },
  { key: 'fullSalary', label: '100 % salaire' },
  { key: 'mix', label: 'Mix' },
]

const toggleStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'var(--elevated)' : 'transparent',
  border: `1px solid ${active ? 'var(--line-strong)' : 'var(--line)'}`,
  borderRadius: '6px',
  color: active ? 'var(--fg)' : 'var(--muted)',
  fontSize: '0.75rem',
  fontFamily: 'inherit',
  padding: '0.3125rem 0.625rem',
  cursor: 'pointer',
})

function Row({
  label,
  monthly,
  tone = 'charge',
  strong,
  weight,
  testId,
  info,
}: {
  label: string
  monthly: number
  /** 'charge' = prélèvement, 'expense' = charge d'exploitation, 'total' = sous-total. */
  tone?: 'charge' | 'expense' | 'total' | undefined
  strong?: boolean | undefined
  /** Poids dans le total des prélèvements (0..1), pour la jauge. */
  weight?: number | undefined
  testId?: string | undefined
  info?: string | undefined
}) {
  const color = tone === 'expense' ? 'var(--bronze)' : tone === 'total' ? 'var(--fg)' : 'var(--charge)'
  const labelColor = tone === 'total' ? 'var(--fg)' : 'var(--muted)'
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
        fontSize: '0.8125rem',
        padding: tone === 'total' ? '0.4rem 0 0.15rem 0.5rem' : '0.15rem 0 0.15rem 0.5rem',
        borderLeft: `2px solid ${tone === 'charge' ? 'var(--charge-bg)' : 'transparent'}`,
        ...(tone === 'total' ? { borderTop: '1px solid var(--line)', marginTop: '0.2rem' } : {}),
      }}
    >
      <span
        style={{
          color: labelColor,
          fontWeight: strong ? 600 : 400,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {label}
        {info === undefined ? null : <InfoTip label={label} text={info} />}
        {weight === undefined ? null : (
          <span
            style={{
              display: 'inline-block',
              width: '38px',
              height: '5px',
              borderRadius: '2px',
              background: 'rgba(232, 225, 205, 0.07)',
              overflow: 'hidden',
              marginLeft: '0.4rem',
              flexShrink: 0,
            }}
          >
            <i
              style={{
                display: 'block',
                height: '100%',
                borderRadius: '2px',
                background: 'var(--charge)',
                width: `${Math.min(100, weight * 100)}%`,
              }}
            />
          </span>
        )}
      </span>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontVariantNumeric: 'tabular-nums',
          color,
          fontWeight: strong ? 600 : 400,
          whiteSpace: 'nowrap',
        }}
      >
        {fmtEur(monthly)}
        <span style={{ color: 'var(--dim)' }}> /mois</span> · {fmtEur(round2(monthly * 12))}
        <span style={{ color: 'var(--dim)' }}> /an</span>
      </span>
    </div>
  )
}

const groupTitle: React.CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
}

export function WhoPaysWhat({
  direction,
  regimes,
  personalExpensesMonthly = 0,
  appCommissionMonthly = 0,
  mode,
  regime,
  unreachable = false,
  onModeChange,
  onRegimeChange,
}: {
  direction: FinanceDirection
  regimes: Record<CorporateRegime, Record<ModeKey, RegimeResult>>
  /** Dépenses perso mensualisées, pour la part « train de vie » du donut. */
  personalExpensesMonthly?: number | undefined
  /** Commission Apple du mois : isolée des dépenses saisies dans le donut. */
  appCommissionMonthly?: number | undefined
  /** Vue courante — détenue par la page, qui la persiste dans les settings. */
  mode: ModeKey
  regime: CorporateRegime
  /**
   * true quand aucun CA n'atteint le net visé dans la vue sélectionnée : les
   * montants sont des zéros de repli. On le dit, plutôt que d'énoncer une
   * synthèse de zéros ou d'imputer un découvert à un CA fabriqué.
   */
  unreachable?: boolean | undefined
  onModeChange: (mode: ModeKey) => void
  onRegimeChange: (regime: CorporateRegime) => void
}) {
  const r = regimes[regime][mode]
  const a = aggregatePayers(r)
  const corporateTaxLabel = regime === 'uk' ? 'Corporation Tax UK' : 'IS français'
  // Le donut suit CES deux sélecteurs, tandis que le bloc « Après tes dépenses
  // perso » du calculateur reste sur le mix : sans cette qualification, deux
  // « ce qui te reste » différents cohabitent sous des libellés identiques.
  const scenarioLabel = `${MODES.find((m) => m.key === mode)?.label ?? 'Mix'}, ${regime === 'uk' ? 'UK' : 'France'}`
  const caLabel = direction === 'netToRevenue' ? 'CA requis' : 'CA facturé'
  const deficit = round2(Math.max(0, a.expenses - r.revenueMonthly))
  /** Poids d'un prélèvement dans le total prélevé — échelle commune aux deux groupes. */
  const weightOf = (v: number) => (a.totalLevies > 0 ? v / a.totalLevies : 0)

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>Qui paie quoi</h3>
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {MODES.map((m) => (
            <button key={m.key} type="button" onClick={() => onModeChange(m.key)} style={toggleStyle(mode === m.key)}>
              {m.label}
            </button>
          ))}
          <span style={{ width: '1px', background: 'var(--line)' }} />
          <button type="button" onClick={() => onRegimeChange('uk')} style={toggleStyle(regime === 'uk')}>
            UK
          </button>
          <button type="button" onClick={() => onRegimeChange('fr')} style={toggleStyle(regime === 'fr')}>
            France
          </button>
        </div>
      </div>

      {unreachable ? (
        <p
          data-testid="whopays-unreachable"
          style={{ fontSize: '0.75rem', color: 'var(--arancio)', margin: 0 }}
        >
          Cible inatteignable dans ce scénario : il n’y a pas de répartition à montrer.
        </p>
      ) : (
        <RevenueDonut
          data={donutData(r, personalExpensesMonthly, corporateTaxLabel, scenarioLabel, appCommissionMonthly)}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <div style={groupTitle}>La société paie</div>
        <Row
          label={corporateTaxLabel}
          monthly={a.company.corporateTax}
          weight={weightOf(a.company.corporateTax)}
          testId="row-corporate-tax"
          info={regime === 'uk' ? TAX_INFO.corporationTaxUk : TAX_INFO.corporationTaxFr}
        />
        <Row
          label="Cotisations patronales (URSSAF)"
          monthly={a.company.employerContrib}
          weight={weightOf(a.company.employerContrib)}
          info={TAX_INFO.employerContrib}
        />
        <Row label="Sous-total société" monthly={a.company.subtotal} tone="total" strong />
        {/* Libellé court : au-delà, la ligne se casse en deux et désaligne l'infobulle.
            La couleur bronze porte désormais la distinction « ce n'est pas un prélèvement ». */}
        <Row label="Charges d'exploitation (hors prélèvements)" monthly={a.expenses} tone="expense" info={TAX_INFO.expenses} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <div style={groupTitle}>Toi, en perso (France)</div>
        <Row
          label="Cotisations salariales"
          monthly={a.personal.employeeContrib}
          weight={weightOf(a.personal.employeeContrib)}
          info={TAX_INFO.employeeContrib}
        />
        <Row
          label="Impôt sur le revenu (barème, quotient)"
          monthly={a.personal.ir}
          weight={weightOf(a.personal.ir)}
          info={TAX_INFO.ir}
        />
        <Row
          label="PFU sur dividendes"
          monthly={a.personal.pfu}
          weight={weightOf(a.personal.pfu)}
          testId="row-pfu"
          info={TAX_INFO.pfu}
        />
        <Row label="Sous-total perso" monthly={a.personal.subtotal} tone="total" strong />
      </div>

      <div
        style={{
          background: 'var(--net-bg)',
          border: '1px solid var(--net-line)',
          borderRadius: '8px',
          padding: '0.85rem 1rem',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          {/* Le chiffre le plus visible de la page était le seul reste NON qualifié :
              lu seul, il contredisait la carte de scénario affichée juste au-dessus. */}
          <div
            data-testid="whopays-net-label"
            style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--net)' }}
          >
            Il te reste, net d’impôts — {scenarioLabel}
          </div>
          <div style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--net)', fontFamily: 'var(--mono)', lineHeight: 1.1 }}>
            {fmtEur(a.net)}
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 500 }}> /mois</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--muted)' }}>
          <div>{fmtEur(round2(a.net * 12))} /an</div>
          <div style={{ marginTop: '0.2rem' }}>
            Prélèvements : <b style={{ color: 'var(--charge)', fontFamily: 'var(--mono)' }}>{fmtEur(a.totalLevies)}</b>
            {a.leviesRate === null ? '' : ` — ${(a.leviesRate * 100).toFixed(1).replace('.', ',')} % du CA`}
          </div>
        </div>
      </div>

      {/* À perte, la forme « CA − prélèvements − dépenses ≈ net » présentait une
          soustraction valant −1 000 comme une approximation de 0 €. On énonce
          alors le déficit, sans « ≈ » ni net factice. */}
      <p data-testid="whopays-summary" style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0 }}>
        {unreachable ? (
          <>
            Cible inatteignable avec ces paramètres fiscaux : aucun chiffre d’affaires n’atteint ce
            net dans ce scénario. Les lignes ci-dessus sont vides, pas nulles.
          </>
        ) : r.isLoss ? (
          <>
            {caLabel} {fmtEur(r.revenueMonthly)} − dépenses {fmtEur(a.expenses)} = déficit{' '}
            <b style={{ color: 'var(--arancio)' }}>{fmtEur(-deficit)}</b>. Le CA ne couvre pas les dépenses société :
            aucun revenu personnel n’est possible.
          </>
        ) : (
          <>
            {caLabel} {fmtEur(r.revenueMonthly)} − prélèvements {fmtEur(a.totalLevies)} − dépenses{' '}
            {fmtEur(a.expenses)} ≈ net {fmtEur(a.net)}.
          </>
        )}
      </p>
    </section>
  )
}
